import { Link, useLocation, useNavigate } from "react-router-dom";
import { Zap, LogOut, Files, LayoutGrid, Users, Settings as SettingsIcon, Check, ChevronDown, User, Tag, WifiOff, Gift } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useOnline } from "../hooks/usePWA";
import { Button } from "./ui/button";
import { BrandMark } from "./Logo";
import InstallButton from "./InstallButton";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";

function syncedAgo() {
  const t = Number(localStorage.getItem("facio_synced_at") || 0);
  if (!t) return "a while ago";
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  return `${hrs}h ago`;
}

export default function Navbar() {
  const { user, login, logout, switchWorkspace } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const online = useOnline();

  const activeWs = user?.active_workspace;
  const credits = activeWs ? user?.effective_credits : user?.credits;
  const workspaces = user?.workspaces || [];

  const doSwitch = async (id) => {
    try {
      await switchWorkspace(id);
      toast.success(id ? "Switched workspace" : "Switched to Personal");
    } catch (e) { toast.error("Could not switch"); }
  };

  const navItem = (to, label, Icon) => {
    const active = location.pathname === to;
    return (
      <Link
        to={to}
        data-testid={`nav-link-${label.toLowerCase()}`}
        className={`hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          active ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
        }`}
      >
        <Icon className="h-4 w-4" /> {label}
      </Link>
    );
  };

  return (
    <header className="sticky top-0 z-50 h-16 backdrop-blur-md bg-white/80 border-b border-slate-200">
      <div className="max-w-7xl mx-auto h-full px-4 sm:px-6 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2">
          <BrandMark className="h-10 w-10" />
          <span className="font-display font-bold text-xl tracking-tight">Facio Doc</span>
        </Link>

        {user && (
          <nav className="flex items-center gap-1">
            {navItem("/studio", "Studio", LayoutGrid)}
            {navItem("/documents", "Documents", Files)}
          </nav>
        )}

        <div className="flex items-center gap-2 sm:gap-3">
          <InstallButton className="hidden sm:inline-flex" />
          {user ? (
            <>
              {/* Workspace switcher */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="workspace-switcher" className="hidden sm:flex items-center gap-1.5 text-sm font-medium bg-slate-100 hover:bg-slate-200 rounded-full pl-3 pr-2 py-1.5 transition-colors">
                    {activeWs ? <Users className="h-4 w-4 text-indigo-600" /> : <User className="h-4 w-4 text-slate-500" />}
                    <span className="max-w-[120px] truncate">{activeWs ? activeWs.name : "Personal"}</span>
                    <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Switch context</DropdownMenuLabel>
                  <DropdownMenuItem data-testid="switch-personal" onClick={() => doSwitch(null)}>
                    <User className="h-4 w-4 mr-2" /> Personal
                    {!activeWs && <Check className="h-4 w-4 ml-auto text-indigo-600" />}
                  </DropdownMenuItem>
                  {workspaces.map((w) => (
                    <DropdownMenuItem key={w.id} data-testid={`switch-ws-${w.id}`} onClick={() => doSwitch(w.id)}>
                      <Users className="h-4 w-4 mr-2" /> <span className="truncate">{w.name}</span>
                      {activeWs?.id === w.id && <Check className="h-4 w-4 ml-auto text-indigo-600" />}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="manage-workspaces" onClick={() => navigate("/workspaces")}>
                    <Users className="h-4 w-4 mr-2" /> Manage workspaces
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <button
                data-testid="navbar-credits-badge"
                onClick={() => navigate("/buy-credits")}
                className="flex items-center gap-1.5 font-mono text-sm font-semibold bg-slate-900 text-white pl-3 pr-1.5 py-1.5 rounded-full hover:bg-slate-800 transition-colors"
              >
                <Zap className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />
                {credits}
                <span className="ml-1 bg-indigo-600 text-white text-xs px-2 py-0.5 rounded-full">Top Up</span>
              </button>
              {!online && (
                <span
                  data-testid="offline-synced-badge"
                  title={`Balance last synced ${syncedAgo()}`}
                  className="hidden sm:inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full"
                >
                  <WifiOff className="h-3 w-3" /> synced {syncedAgo()}
                </span>
              )}
              {activeWs && (
                <span data-testid="team-cost-badge" className="hidden md:inline-flex items-center gap-1 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 px-2.5 py-1 rounded-full">
                  <Users className="h-3 w-3" /> +{user?.pricing?.team_premium_applied ?? 1} team credit
                </span>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="navbar-avatar" className="h-9 w-9 rounded-full overflow-hidden ring-2 ring-slate-200 hover:ring-indigo-300 transition">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="h-full w-full object-cover" />
                    ) : (
                      <div className="h-full w-full bg-indigo-600 text-white flex items-center justify-center font-semibold">
                        {(user.name || user.email || "U")[0].toUpperCase()}
                      </div>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-semibold truncate">{user.name}</p>
                    <p className="text-xs text-slate-500 truncate">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/documents")} data-testid="menu-documents">
                    <Files className="h-4 w-4 mr-2" /> My Documents
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/workspaces")} data-testid="menu-workspaces">
                    <Users className="h-4 w-4 mr-2" /> Workspaces
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/buy-credits")} data-testid="menu-buy-credits">
                    <Zap className="h-4 w-4 mr-2" /> Buy Credits
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/referrals")} data-testid="menu-referrals">
                    <Gift className="h-4 w-4 mr-2" /> Refer &amp; earn
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/pricing")} data-testid="menu-pricing">
                    <Tag className="h-4 w-4 mr-2" /> Pricing & Limits
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                    <SettingsIcon className="h-4 w-4 mr-2" /> Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-red-600 focus:text-red-600">
                    <LogOut className="h-4 w-4 mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Button
              data-testid="navbar-login-button"
              onClick={login}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-5"
            >
              Sign in with Google
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
